import {
  IconActivityLog,
  IconAlertConnection,
  IconAlertCoverage,
  IconAlertRoute,
  IconAlertRun,
  IconAlertTarget,
  IconAiDocument,
  IconAnalytic,
  IconAnalyticCustom,
  IconAnalyticGeneral,
  IconAnalyticsSetup,
  IconApiKeyOversight,
  IconApp,
  IconAssetLibrary,
  IconAuditRetention,
  IconAuthSession,
  IconBilling,
  IconBug,
  IconConsentLog,
  IconContent,
  IconDashboard,
  IconEmail,
  IconEvent,
  IconGroup,
  IconLink,
  IconList,
  IconLmsPresets,
  IconNotification,
  IconOrganization,
  IconOrganizationRolePermissions,
  IconOrganizationRoles,
  IconPackage,
  IconPermission,
  IconRefusedSignIn,
  IconRefusedSignInLog,
  IconRole,
  IconSelfRegister,
  IconSettings,
  IconShield,
  IconSignInMethod,
  IconSsoConnection,
  IconSysLogin,
  IconSystemAdmins,
  IconSystemRoles,
  IconTemplate,
  IconUsers
} from '../icon/icon'

// One definition per sidebar entry that opens a page: the page's url, its
// name and its icon. The sidebar draws its entries from these
// (page/component/sidebar-left.js), and the page at the top of a section --
// or a page that names the section as its `parent` -- takes its crumb from
// the same one, so a section's crumb and its sidebar entry can't drift apart
// (owner 2026-11-03, audit F9/F13; sections.test.js holds both sides to it).
//
// Shape: { url, text, Icon } -- a crumb's (BreadcrumbView's `parent`, a
// PageTrail entry) -- and, for the sidebar only:
//   title    the entry's label where it is shorter than the page's name
//            (Analytics > General is the page "General analytics");
//   to       where the entry goes when that isn't the section's url (LMS
//            insights open on their first tab);
//   section  the paths the entry stays highlighted on (each and the pages
//            under it), when they are more than its own url's;
//   claims   an address the entry is lit on although another's path matches
//            it (Connect opened from an organization is the organization's).
const section = (url, text, Icon, more) => ({ url, text, Icon, ...more })

export const SECTION = {
  // The organization's menu
  dashboard: section('/org/dashboard', 'Dashboard', IconDashboard),
  analyticsGeneral: section('/org/analytic/general', 'General analytics', IconAnalyticGeneral, { title: 'General' }),
  analyticsCustom: section('/org/analytic/custom', 'Custom analytics', IconAnalyticCustom, { title: 'Custom' }),
  analyticsLms: section('/org/analytic/lms', 'LMS insights', IconLink, { title: 'LMS', to: '/org/analytic/lms/launches' }),
  content: section('/org/node-content', 'Content', IconContent),
  templates: section('/org/node-template', 'Templates', IconTemplate),
  assetLibrary: section('/org/node-library', 'Asset library', IconAssetLibrary),
  users: section('/org/user/list', 'Users', IconUsers),
  groups: section('/org/group/list', 'Groups', IconGroup),
  selfRegister: section('/org/self-register/list', 'Self register', IconSelfRegister),
  auditLog: section('/org/audit-log', 'Audit log', IconActivityLog),
  emailActivity: section('/org/email-activity', 'Email activity', IconEmail),
  notificationActivity: section('/org/notification-activity', 'Notification activity', IconNotification),
  apiKeys: section('/org/api-keys', 'API keys', IconPermission),
  // Integrations > LMS opens nothing itself; its pages are under it.
  lms: section('/org/lms', 'LMS', IconLink),
  lmsConnections: section('/org/lms/connections', 'Connections', IconLink),
  // A package's build log is opened from its course (audit F5).
  lmsCourses: section('/org/lms/courses', 'Courses', IconContent, { section: ['/org/lms/courses', '/org/lms/packages'] }),
  lmsAccountLinks: section('/org/lms/account-links', 'Account linking', IconUsers),
  lmsSessions: section('/org/lms/sessions', 'Sessions', IconApp),
  lmsActivity: section('/org/lms/activity', 'Activity', IconEvent),
  lmsUsage: section('/org/lms/usage', 'Plan & usage', IconAnalytic),
  lmsSettings: section('/org/lms/settings', 'Settings', IconSettings),
  organization: section('/org/organization', 'Organization', IconOrganization),
  authentication: section('/org/sso', 'Authentication', IconShield),

  // Internal tools
  // Connect opened from an organization's SSO tab reads Organizations > <name>
  // > SSO > Connect single sign-on (provider-connect.js).
  organizations: section('/admin/organization/list', 'Organizations', IconOrganization, {
    claims: ({ pathname, search }) => pathname === '/admin/sso/connect' && new URLSearchParams(search).has('organizationID')
  }),
  logins: section('/admin/login/list', 'Logins', IconSysLogin),
  authSessions: section('/admin/auth-session/list', 'Auth sessions', IconAuthSession),
  // The SSO pages beside the list (domains, health, findings, jobs, connect...)
  // are the section's: the trail starts at SSO connections (audit F5).
  ssoConnections: section('/admin/sso/connections', 'SSO connections', IconSsoConnection, { section: '/admin/sso' }),
  // One method is /admin/signin-methods/:kind (signin-method-screen.js).
  signInMethods: section('/admin/signin-method', 'Sign-in methods', IconSignInMethod, { section: ['/admin/signin-method', '/admin/signin-methods'] }),
  refusedSignIns: section('/admin/device-login-denial/summary', 'Refused sign-ins', IconRefusedSignIn),
  refusedSignInLog: section('/admin/device-login-denial/list', 'Refused sign-in log', IconRefusedSignInLog),
  // Under Sign-in & SSO; its page is not under /admin/sso/connections, so it
  // claims its own address over SSO connections' /admin/sso.
  ssoAlertCoverage: section('/admin/sso/alert-coverage', 'SSO alert coverage', IconAlertCoverage),
  errors: section('/admin/errors', 'Errors', IconBug),
  requestLog: section('/admin/request-log', 'Request log', IconList),
  staffAuditLog: section('/admin/audit-log', 'Audit log', IconActivityLog),
  auditRetention: section('/admin/audit-retention', 'Audit retention', IconAuditRetention),
  // Alert routing: a page each (owner 2026-10-30, alert-routing/paths.js).
  alertRoutes: section('/admin/alert-routing/routes', 'Alert routes', IconAlertRoute),
  alertTargets: section('/admin/alert-routing/targets', 'Alert targets', IconAlertTarget),
  alertConnections: section('/admin/alert-routing/connections', 'Alert connections', IconAlertConnection),
  alertRuns: section('/admin/alert-routing/runs', 'Alert runs', IconAlertRun),
  staffEmailActivity: section('/admin/email-activity', 'Email activity', IconEmail),
  notifications: section('/admin/notify', 'Notifications', IconNotification),
  apps: section('/admin/app', 'Apps', IconApp),
  staffApiKeys: section('/admin/api-keys', 'API keys', IconApiKeyOversight),
  aiTools: section('/admin/ai-tools', 'AI tools telemetry', IconAiDocument),
  packages: section('/admin/packages', 'Packages', IconPackage),
  billing: section('/admin/billing', 'Billing', IconBilling),
  consentLog: section('/admin/consent-log', 'Consent log', IconConsentLog),
  lmsPresets: section('/org/lms/presets', 'LMS presets', IconLmsPresets),
  systemRoles: section('/root/role-sys/list', 'System roles', IconSystemRoles),
  systemRolePermissions: section('/root/role-sys-permission/list', 'System role permissions', IconRole),
  organizationRoles: section('/root/role-org/list', 'Organization roles', IconOrganizationRoles),
  organizationRolePermissions: section('/root/role-org-permission/list', 'Organization role permissions', IconOrganizationRolePermissions),
  systemAdmins: section('/root/sys-admin/list', 'System admins', IconSystemAdmins),
  analyticsSetup: section('/root/analytic-configure', 'Analytics setup', IconAnalyticsSetup)
}

// A sidebar entry for a section: its label, icon, link and highlight.
export const sidebarEntry = (definition, more) => ({
  title: definition.title || definition.text,
  Icon: definition.Icon,
  to: definition.to || definition.url,
  section: definition.section || (definition.to ? definition.url : undefined),
  claims: definition.claims,
  ...more
})

// The paths an entry is lit on, each with the pages under it.
const litPaths = entry => [].concat(entry.section || entry.to).filter(Boolean)

const isUnder = (pathname, path) => pathname === path || String(pathname || '').startsWith(`${path}/`)

// The one entry the current address belongs to: one that claims it, else
// the one whose path matches it most closely (/admin/sso/connections/7 is
// SSO connections', /org/audit-log the organization's Audit log).
export const selectedEntry = (entries, location) => {
  const { pathname = '', search = '' } = location || {}
  const claimed = entries.find(entry => entry.claims && entry.claims({ pathname, search }))
  if (claimed) return claimed
  let best = null
  let length = -1
  entries.forEach(entry => litPaths(entry).forEach(path => {
    if (isUnder(pathname, path) && path.length > length) {
      best = entry
      length = path.length
    }
  }))
  return best
}

// A staff page outside /admin and /root: the platform's LMS presets
// (page/component/staff-tools.js turns Internal tools on for it).
export const STAFF_PAGES_ELSEWHERE = [SECTION.lmsPresets.url]
